
import { useCanvasStore } from '../../stores/canvasStore.js';
import type { Widget, Connection, Position, WidgetConfig } from '../../types/index.js';
import { widgetRegistry } from '../../utils/widgetRegistry.js';

interface WidgetCreationResult {
  success: boolean;
  widgetId?: string;
  error?: string;
  widget?: Widget;
}

interface ConnectionCreationResult {
  success: boolean;
  connectionId?: string;
  error?: string;
  connection?: Connection;
}

/**
 * Canvas Architect Agent
 * Responsible for creating, connecting, and managing widgets on the canvas
 */
class CanvasArchitect {
  // Optional override to force operations to a specific canvas
  private forcedCanvasId?: string;

  setTargetCanvas(canvasId?: string) {
    this.forcedCanvasId = canvasId;
  }

  private getTargetCanvasId(): string | undefined {
    try {
      const store = useCanvasStore.getState();
      return this.forcedCanvasId || store.activeCanvasId;
    } catch {
      return this.forcedCanvasId;
    }
  }
  /**
   * Creates a new widget on the canvas
   * This method is called by tool execution from the orchestrator
   */
  createWidget(type: string, position?: Position, config?: Partial<WidgetConfig>): WidgetCreationResult {
    try {
      console.log('[CanvasArchitect] Creating widget:', { type, position, config });
      
      const store = useCanvasStore.getState();
      const activeCanvasId = this.getTargetCanvasId();
      
      if (!activeCanvasId) {
        console.error('[CanvasArchitect] No active canvas');
        return {
          success: false,
          error: 'No active canvas found'
        };
      }

      // Use provided position or calculate a smart position
      const finalPosition = position || this.calculateSmartPosition();
      
      // Merge config with title if provided
      const finalConfig = {
        ...config,
        title: config?.title || `New ${type} Widget`
      };

      // Add the widget to the store - this returns the widget ID
      const widgetId = store.addWidget(type, finalPosition, finalConfig);
      
      // Get the created widget for return
      const canvas = store.canvases.find(c => c.id === activeCanvasId);
      const widget = canvas?.widgets.find(w => w.id === widgetId);
      
      console.log('[CanvasArchitect] Widget created successfully:', widgetId);
      
      return {
        success: true,
        widgetId,
        widget
      };
    } catch (error) {
      console.error('[CanvasArchitect] Error creating widget:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error creating widget'
      };
    }
  }

  /**
   * Connects two widgets on the canvas.
   * This function contains enhanced logic to handle "connection groups"
   * for widgets that require multiple connections to be made at once.
   */
  connectWidgets(
    sourceWidgetId: string,
    targetWidgetId: string,
    sourceOutput?: string,
    targetInput?: string
  ): ConnectionCreationResult {
    try {
      const store = useCanvasStore.getState();
      const activeCanvasId = this.getTargetCanvasId();
      if (!activeCanvasId) {
        return { success: false, error: 'No active canvas found' };
      }

      const canvas = store.canvases.find(c => c.id === activeCanvasId);
      if (!canvas) {
        return { success: false, error: 'Canvas not found' };
      }

      const sourceWidget = canvas.widgets.find(w => w.id === sourceWidgetId);
      const targetWidget = canvas.widgets.find(w => w.id === targetWidgetId);

      if (!sourceWidget || !targetWidget) {
        return { success: false, error: `Widget not found: ${!sourceWidget ? 'source' : 'target'}` };
      }

      const sourceDef = widgetRegistry.get(sourceWidget.type);
      const targetDef = widgetRegistry.get(targetWidget.type);

      if (!sourceDef || !targetDef) {
        return { success: false, error: 'Widget definition not found in registry' };
      }

      // Check for a connection group match
      const connectionGroup = targetDef.connectionGroups?.find(
        (group: any) => group.sourceWidgetType === sourceDef.type
      );

      if (connectionGroup) {
        console.log(`[CanvasArchitect] Found connection group: "${connectionGroup.description}"`);
        // Create all connections defined in the group
        connectionGroup.connections.forEach((conn: any) => {
          store.createConnection(activeCanvasId, {
            source: { widgetId: sourceWidgetId, outputId: conn.from },
            target: { widgetId: targetWidgetId, inputId: conn.to },
          });
        });
        
        // For now, we return the ID of the last connection for simplicity.
        // A more robust solution might return all created connection IDs.
        const lastConnectionId = canvas.connections[canvas.connections.length - 1]?.id;
        return {
          success: true,
          connectionId: lastConnectionId,
          connection: canvas.connections.find(c => c.id === lastConnectionId),
        };

      } else {
        // Fallback to original logic if no group is found
        console.log('[CanvasArchitect] No connection group found, using default connection logic.');
        const finalSourceOutput = sourceOutput || sourceDef.outputDefinitions[0]?.id || 'output';
        const finalTargetInput = targetInput || targetDef.inputDefinitions[0]?.id || 'input';

        const connectionId = store.createConnection(activeCanvasId, {
          source: { widgetId: sourceWidgetId, outputId: finalSourceOutput },
          target: { widgetId: targetWidgetId, inputId: finalTargetInput },
        });
        
        const connection = canvas.connections.find(c => c.id === connectionId);
        return { success: true, connectionId, connection };
      }
    } catch (error) {
      console.error('[CanvasArchitect] Error connecting widgets:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error connecting widgets',
      };
    }
  }

// ... (rest of the class)

  /**
   * Gets a widget from the canvas
   */
  getWidget(widgetId: string): Widget | null {
    const store = useCanvasStore.getState();
    const activeCanvasId = this.getTargetCanvasId();
    
    if (!activeCanvasId) {
      console.error('[CanvasArchitect] No active canvas');
      return null;
    }

    const canvas = store.canvases.find(c => c.id === activeCanvasId);
    return canvas?.widgets.find(w => w.id === widgetId) || null;
  }

  /**
   * Updates a widget's configuration
   */
  updateWidgetConfig(widgetId: string, config: Partial<WidgetConfig>): boolean {
    try {
      const store = useCanvasStore.getState();
      const activeCanvasId = this.getTargetCanvasId();
      
      if (!activeCanvasId) {
        console.error('[CanvasArchitect] No active canvas');
        return false;
      }

      store.updateWidgetConfig(activeCanvasId, widgetId, config);
      console.log('[CanvasArchitect] Widget config updated:', widgetId);
      return true;
    } catch (error) {
      console.error('[CanvasArchitect] Error updating widget config:', error);
      return false;
    }
  }

  /**
   * Calculates a smart position for new widgets
   * Avoids overlapping with existing widgets
   */
  private calculateSmartPosition(): Position {
    const store = useCanvasStore.getState();
    const activeCanvasId = this.getTargetCanvasId();
    
    if (!activeCanvasId) {
      return { x: 100, y: 100 };
    }

    const canvas = store.canvases.find(c => c.id === activeCanvasId);
    if (!canvas || canvas.widgets.length === 0) {
      return { x: 100, y: 100 };
    }

    // Find the rightmost widget and place new widget to its right
    const rightmostWidget = canvas.widgets.reduce((max, widget) => 
      widget.position.x > max.position.x ? widget : max
    );

    return {
      x: rightmostWidget.position.x + (rightmostWidget.size?.width || 300) + 50,
      y: rightmostWidget.position.y
    };
  }

  /**
   * Gets all widgets on the active canvas
   */
  getAllWidgets(): Widget[] {
    const store = useCanvasStore.getState();
    const activeCanvasId = this.getTargetCanvasId();
    
    if (!activeCanvasId) {
      return [];
    }

    const canvas = store.canvases.find(c => c.id === activeCanvasId);
    return canvas?.widgets || [];
  }

  /**
   * Gets all connections on the active canvas
   */
  getAllConnections(): Connection[] {
    const store = useCanvasStore.getState();
    const activeCanvasId = this.getTargetCanvasId();
    
    if (!activeCanvasId) {
      return [];
    }

    const canvas = store.canvases.find(c => c.id === activeCanvasId);
    return canvas?.connections || [];
  }
}

export const canvasArchitect = new CanvasArchitect();
